import { MONEY_DECIMALS, MoneyAmountSchema, type Locale } from '@aysar/contracts';
import { z, type ZodType } from 'zod';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { formatMoney } from '@/shared/utils/format-money/format-money.util';

import { type DirectPriceDraft } from '../../hooks/useDirectPrices/useDirectPrices';
import {
  DIRECT_PRICE_SCOPE_IDS,
  type EffectivePrice,
} from '../direct-price-scopes/direct-price-scopes.util';

/** Translates a key, with interpolation. The caller's `t`. */
export type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * The price editor's schema — one nullable amount per scope.
 *
 * Built from `MoneyAmountSchema`, so the non-negative and two-decimal rules are
 * the api's own rather than a restatement (U3). `.nullable()` and **not**
 * `.optional()`: an empty box means *this gender has no price of its own*, which
 * is a decision the write acts on (it clears the scope), where an absent key
 * would mean "leave it alone" and there would be no way to express the first.
 * The field defs carry `isEmptyNull` for the same reason.
 *
 * Input and output are both the draft shape — no `.default()` anywhere, which is
 * what `useDynamicFormLogic` requires and refuses a schema without (P20b).
 */
export function directPriceSchema(): ZodType<DirectPriceDraft, DirectPriceDraft> {
  return z
    .object({
      global: MoneyAmountSchema.nullable(),
      male: MoneyAmountSchema.nullable(),
      female: MoneyAmountSchema.nullable(),
    })
    .strict();
}

export interface DirectPriceFieldsInput {
  t: Translate;
  locale: Locale;
  /** ISO 4217 code, or `''` while `/app-settings` is still loading. */
  currency: string;
  /** What each scope resolves to now, and which layer decided it. */
  priceFor: (scope: (typeof DIRECT_PRICE_SCOPE_IDS)[number]) => EffectivePrice;
  isDisabled: boolean;
}

/**
 * One money box per scope, each saying what it currently resolves to and why.
 *
 * ## The description is the whole point of this control
 *
 * A gender with no price of its own is charged what everyone is charged, and
 * the two are indistinguishable by looking at the number: 199 inherited and 199
 * stored render identically, and only one of them follows a change to the global
 * price. So every box names its **source** — set here, inherited, or unpriced —
 * which is what turns three inputs into a legible policy (W8's "every setting
 * shows its scope and its effective resolved value").
 *
 * ## `isEmptyNull`, not `isEmptyAbsent`
 *
 * Emptying a box is a decision the write acts on: it removes that gender's own
 * price, clearing the scope's row entirely when it was the last one. Stripping
 * the key would make "leave it alone" and "stop overriding" the same request,
 * and there would be no way to say the second (P20b's clause, the other way up).
 *
 * ## `decimals` is passed, never defaulted
 *
 * `MONEY_DECIMALS` — the platform's own constant, and the same rounding
 * `MoneyAmountSchema` enforces. A default here would be a silent rounding of
 * somebody's price (P10.5d1).
 */
export function directPriceFields({
  t,
  locale,
  currency,
  priceFor,
  isDisabled,
}: DirectPriceFieldsInput): FieldDef[] {
  return DIRECT_PRICE_SCOPE_IDS.map((scope) => {
    const resolved = priceFor(scope);

    return {
      name: scope,
      kind: 'money' as const,
      label: t(`pricing.scope.${scope}`),
      description: sourceSentence(resolved, { t, locale, currency }),
      // An empty box is "no price of its own", which the write turns into a
      // clear — never an absent key, which would mean "leave it alone".
      isEmptyNull: true,
      isDisabled,
      isReadOnly: isDisabled,
      currency,
      decimals: MONEY_DECIMALS,
      min: 0,
    };
  });
}

function sourceSentence(
  resolved: EffectivePrice,
  { t, locale, currency }: { t: Translate; locale: Locale; currency: string },
): string {
  if (resolved.amount === null) {
    return t('pricing.source.default');
  }

  // The amount is repeated in the sentence deliberately: the box holds the
  // scope's OWN value (empty when it inherits), so without this an inheriting
  // gender would show an empty box and no indication of what it is charged.
  return `${t(`pricing.source.${resolved.source === 'scoped' ? 'scoped' : 'global'}`)} — ${formatMoney(
    resolved.amount,
    locale,
    currency === '' ? null : currency,
  )}`;
}
